import React, { useContext, useMemo } from "react";
import { parseISO } from "date-fns";
import { AmountFilterLevel } from "@common/constants/counterparty";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useWorkspaceAccounts } from "@api/account.api";
import { useLedgersByEntityIds } from "@api/ledger.api";
import { useBusinessNonMockEntityIds } from "@hooks/useBusinessEntityIds";
import { AccountingReportFilters } from "@components/accounting/types";
import { getMultiEntityBasisInfo } from "@components/accounting/generalLedger/multiEntityLedger";
import { GlDrilldownDrawer } from "@components/accounting/generalLedger/GlDrilldownDrawer";
import {
    createDefaultCounterpartyGlDrillDownTransactionView,
    getCounterpartyGlTransactionsDrilldownCacheKey,
} from "@components/transactions/views/DefaultCounterpartyGlDrillDownTransactionView";
import { DirectoryContext } from "./Directory.context";

const counterpartyGlTransactionsDrilldown = {
    createView: createDefaultCounterpartyGlDrillDownTransactionView,
    getCacheKey: getCounterpartyGlTransactionsDrilldownCacheKey,
};

interface Props {
    open: boolean;
    onClose: () => void;
    counterpartyId: string | null;
    isNoCounterparty: boolean;
    /** When set (account-level row), scopes the GL to this specific account. */
    accountCode?: number;
}

export const DirectoryGlDrilldownDrawer: React.FC<Props> = ({
    open,
    onClose,
    counterpartyId,
    isNoCounterparty,
    accountCode,
}) => {
    const { filters: directoryFilters, view } = useContext(DirectoryContext);
    const businessEntityIds = useBusinessNonMockEntityIds();
    const { data: accounts } = useWorkspaceAccounts();

    const entityIds = useMemo(
        () =>
            directoryFilters.entityIds?.length
                ? directoryFilters.entityIds
                : businessEntityIds,
        [directoryFilters.entityIds, businessEntityIds],
    );

    const { data: ledgersByEntityId } = useLedgersByEntityIds(entityIds);

    const ledgerId = useMemo(() => {
        if (!ledgersByEntityId) {
            return undefined;
        }
        const basisInfo = getMultiEntityBasisInfo({
            ledgersByEntityId,
            selectedEntityIds: entityIds,
        });
        return directoryFilters.basis === AccountingLedgerBasis.accruals
            ? basisInfo.accrualLedgerId
            : basisInfo.cashLedgerId;
    }, [ledgersByEntityId, entityIds, directoryFilters.basis]);

    const coaAccountCodes = useMemo(() => {
        // An explicit account code from a child row takes precedence over the
        // view's COA accounts filter — the user clicked on a specific account.
        if (accountCode !== undefined) {
            return [accountCode];
        }
        const coaIds = view.filters.coaAccounts;
        if (!coaIds?.length || !accounts) {
            return undefined;
        }
        const selectedIds = new Set(coaIds);
        const codes = accounts
            .filter((a) => selectedIds.has(a.id))
            .map((a) => a.code);
        return codes.length ? [...new Set(codes)] : undefined;
    }, [accountCode, view.filters.coaAccounts, accounts]);

    const isJournalEntryAmountFilter =
        view.filters.amountFilterLevel === AmountFilterLevel.JOURNAL_ENTRY;

    const filters = useMemo<AccountingReportFilters>(
        () => ({
            ...(directoryFilters.startDate || directoryFilters.endDate
                ? {
                      startDate: directoryFilters.startDate
                          ? parseISO(directoryFilters.startDate)
                          : undefined,
                      endDate: directoryFilters.endDate
                          ? parseISO(directoryFilters.endDate)
                          : undefined,
                  }
                : { isAllTime: true }),
            entityId: entityIds[0],
            accountCodes: coaAccountCodes,
            counterpartiesIds: counterpartyId ? [counterpartyId] : undefined,
            withEmptyOrUnverifiedCounterparty: isNoCounterparty
                ? true
                : undefined,
            ledgerId,
            accountIds: view.filters.accountIds ?? undefined,
            journalEntrySources: view.filters.journalEntrySources,
            minAmount: isJournalEntryAmountFilter
                ? view.filters.minAmount
                : undefined,
            maxAmount: isJournalEntryAmountFilter
                ? view.filters.maxAmount
                : undefined,
            direction: view.filters.direction,
            amountRounded: view.filters.amountRounded,
            amountRoundingLevel: view.filters.amountRoundingLevel,
        }),
        [
            directoryFilters.startDate,
            directoryFilters.endDate,
            entityIds,
            coaAccountCodes,
            counterpartyId,
            isNoCounterparty,
            ledgerId,
            view.filters.accountIds,
            view.filters.journalEntrySources,
            isJournalEntryAmountFilter,
            view.filters.minAmount,
            view.filters.maxAmount,
            view.filters.direction,
            view.filters.amountRounded,
            view.filters.amountRoundingLevel,
        ],
    );

    return (
        <GlDrilldownDrawer
            open={open}
            onClose={onClose}
            filters={filters}
            entityIds={entityIds}
            ledgerBasis={directoryFilters.basis}
            size="fullScreen"
            transactionsDrilldown={counterpartyGlTransactionsDrilldown}
        />
    );
};
